/**
 * Извлекает текст, иконку и ссылку из строки формата "текст;;;иконка;;;ссылка".
 * Возвращает null, если входное значение не является строкой или не соответствует формату.
 *
 * @param {string} value - Строка для разбора
 * @returns { {text: string; icon: string; href: string} | null } Объект с извлеченными данными или null
 * @example
 * extractNameAndIconForHref("Документ;;;doc-icon;;;/docs/123");
 * { text: "Документ", icon: "doc-icon", href: "/docs/123" }
 */
const extractNameAndIconForHref = (value: string) => {
    if (typeof value !== 'string') return null;

    const match = /^([^;]*);;;([^;]*);;;([^;]*)$/.exec(value);

    if (!match) return null;

    return {
        text: match[1],
        icon: match[2],
        href: match[3],
    };
};

/**
 * Извлекает текст и ссылку из строки формата "текст;;;ссылка".
 * Возвращает null, если строка не соответствует формату.
 *
 * @param {string} value - Строка для разбора
 * @returns { {text: string; href: string} | null } Объект с извлеченными данными или null
 * @example
 * extractNameForHref("Профиль;;;/users/42"); // { text: "Профиль", href: "/users/42" }
 */
const extractNameForHref = (value: string) => {
    if (typeof value !== 'string') return null;

    const match = /^([^;]*);;;([^;]*)$/.exec(value);

    if (!match) return null;

    return {
        text: match[1],
        href: match[2],
    };
};

interface IReportColumnURLInfo {
    text: string;
    href?: string;
    icon?: string;
}

/**
 * Извлекает информацию о ссылке из сырого значения, поддерживая несколько форматов:
 * 1. "текст;;;иконка;;;ссылка"
 * 2. "текст;;;ссылка"
 * 3. Ссылка без текста (начинается с http)
 * 4. Простой текст без ссылки
 *
 * @param {string | undefined} rawValue - Входное значение для разбора
 * @returns {IReportColumnURLInfo | undefined} Объект с информацией о ссылке или undefined для null/undefined ввода
 * @example
 * extractUrlInfo("Отчет;;;report-icon;;;/reports/1");
 * { text: "Отчет", icon: "report-icon", href: "/reports/1" }
 *
 * extractUrlInfo("https://example.com");
 * { text: "", href: "https://example.com", icon: "Document" }
 *
 * extractUrlInfo("Простой текст");
 * { text: "Простой текст" }
 */
export const extractUrlInfo = (rawValue: string | undefined) => {
    if (rawValue === undefined || rawValue === null) return undefined;

    const urlInfo: IReportColumnURLInfo = (() => {
        const iconAndNameExtraction = extractNameAndIconForHref(rawValue);
        if (iconAndNameExtraction) return iconAndNameExtraction;

        const nameExtraction = extractNameForHref(rawValue);
        if (nameExtraction) return nameExtraction;

        if (rawValue.startsWith('http')) {
            return {text: '', href: rawValue, icon: 'Document'};
        }

        return {text: rawValue};
    })();

    return urlInfo;
};
